import React, { useState } from 'react';
import { X, Search, Book, Headphones, Tablet, Loader2, Camera } from 'lucide-react';
import { useBooks } from '../context/BookContext';
import BarcodeScanner from './BarcodeScanner';
import { MOODS } from '../utils/moodQueries';

const AddBookModal = ({ onClose }) => {
  const { addBook, searchCachedBooks, searchCachedByGenre, lookupCachedISBN, saveToCache } = useBooks();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [activeMood, setActiveMood] = useState(null);
  
  // Form State
  const [status, setStatus] = useState('tbr');
  const [rating, setRating] = useState(0);
  const [format, setFormat] = useState('physical');
  const [totalPages, setTotalPages] = useState('');
  const [audioHours, setAudioHours] = useState('');
  const [audioMinutes, setAudioMinutes] = useState('');

  const cacheGoogleItem = (item) => {
    const info = item.volumeInfo;
    saveToCache({
      external_id: item.id,
      title: info.title,
      author: info.authors ? info.authors[0] : 'Unknown',
      cover: info.imageLinks?.thumbnail?.replace('http:', 'https:'),
      totalPages: info.pageCount || 0,
      genre: info.categories ? info.categories[0] : 'General',
      isbn: info.industryIdentifiers?.find(i => i.type.includes('ISBN'))?.identifier
    });
  };

  // Pulls the full catalog for a given author. When silent, just warms the
  // cache in the background (used after a book is selected); otherwise it
  // also drives the visible search results (used when an author is clicked).
  const fetchAuthorBooks = async (authorName, { silent = false } = {}) => {
    if (!authorName || authorName === 'Unknown') return;
    if (!silent) setLoading(true);
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(`inauthor:"${authorName}"`)}&maxResults=40${apiKey ? `&key=${apiKey}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      const googleItems = data.items || [];

      googleItems.forEach(cacheGoogleItem);

      if (!silent) {
        setQuery(authorName);
        setResults(googleItems);
      }
    } catch (error) {
      console.error("Author lookup failed", error);
    }
    if (!silent) setLoading(false);
  };

  const searchBooks = async () => {
    if (!query) return;
    setActiveMood(null);
    setLoading(true);
    try {
      // 1. Check our own cache first
      const cached = await searchCachedBooks(query);

      // 2. Always fetch from Google to ensure fresh data, but we could skip if cached is enough
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(query)}&maxResults=20${apiKey ? `&key=${apiKey}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        // If Google fails, use ONLY cached results
        if (cached && cached.length > 0) {
          setResults(cached.map(b => ({ id: b.external_id, volumeInfo: b, fromCache: true })));
        } else {
          alert("Search limit reached and no cached results found. Please try again later.");
        }
      } else {
        // Merge results, prioritizing Google but adding unique cached items
        const googleItems = data.items || [];

        // Save new results to cache in the background
        googleItems.forEach(cacheGoogleItem);

        setResults(googleItems);
      }
    } catch (error) {
      console.error("Search failed", error);
    }
    setLoading(false);
  };


  // "Vibe" search: run a mood's pre-built Google Books subject/keyword query
  // instead of a free-text title/author search. Reuses the exact same
  // fetch + book_cache upsert pipeline as searchBooks/fetchAuthorBooks so
  // mood results get cached for everyone the same way normal search does.
  const searchByMood = async (mood) => {
    setActiveMood(mood.key);
    setQuery('');
    setLoading(true);
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(mood.query)}&maxResults=20${apiKey ? `&key=${apiKey}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        // Google quota/limit hit - fall back to whatever matches this
        // mood's genre in book_cache, same degraded-mode idea as searchBooks.
        const cached = await searchCachedByGenre(mood.genreHint);
        if (cached && cached.length > 0) {
          setResults(cached.map(b => ({ id: b.external_id, volumeInfo: b, fromCache: true })));
        } else {
          alert("Search limit reached and no cached results found for this vibe yet. Please try again later.");
        }
      } else {
        const googleItems = data.items || [];
        googleItems.forEach(cacheGoogleItem);
        setResults(googleItems);
      }
    } catch (error) {
      console.error("Mood search failed", error);
    }
    setLoading(false);
  };

  const handleBarcodeScan = async (isbn) => {
    setIsScanning(false);
    setLoading(true);
    try {
      // 1. Check Cache
      const cached = await lookupCachedISBN(isbn);
      if (cached) {
        handleSelect({ volumeInfo: cached });
        setLoading(false);
        return;
      }

      // 2. Fetch from Google
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}${apiKey ? `&key=${apiKey}` : ''}`);
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        const item = data.items[0];
        handleSelect(item);
        // Save to cache
        const info = item.volumeInfo;
        saveToCache({
          external_id: item.id,
          title: info.title,
          author: info.authors ? info.authors[0] : 'Unknown',
          cover: info.imageLinks?.thumbnail?.replace('http:', 'https:'),
          totalPages: info.pageCount || 0,
          genre: info.categories ? info.categories[0] : 'General',
          isbn: isbn
        });
      } else {
        alert("No book found for this barcode. Try manual search.");
      }
    } catch (error) {
      console.error("Barcode lookup failed", error);
    }
    setLoading(false);
  };

  const handleSelect = (book) => {
    const info = book.volumeInfo;
    const authorName = Array.isArray(info.authors) ? info.authors[0] : (info.author || 'Unknown');
    setSelectedBook({
      title: info.title,
      author: authorName,
      cover: info.imageLinks?.thumbnail?.replace('http:', 'https:') || info.cover,
      totalPages: info.pageCount || info.totalPages || 0,
      genre: Array.isArray(info.categories) ? info.categories[0] : (info.genre || 'General')
    });
    setTotalPages(info.pageCount || info.totalPages || '');
    setResults([]);
    // Warm the cache with the rest of this author's catalog in the background
    fetchAuthorBooks(authorName, { silent: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBook) return;

    let finalAudioMinutes = 0;
    if (format === 'audio') {
      finalAudioMinutes = (parseInt(audioHours || 0) * 60) + parseInt(audioMinutes || 0);
    }

    let finalTotalPages = parseInt(totalPages || 0);
    if (format === 'audio' && finalAudioMinutes > 0) {
      finalTotalPages = 0; 
    }

    const newBook = {
      title: selectedBook.title,
      author: selectedBook.author,
      cover: selectedBook.cover,
      genre: selectedBook.genre,
      status, 
      rating: status === 'read' ? rating : 0,
      format,
      totalPages: finalTotalPages,
      audio_minutes: finalAudioMinutes
    };

    await addBook(newBook);
    onClose();
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <button onClick={onClose} style={closeBtn}><X size={24} /></button>
        
        <h2 style={{marginTop: 0, color: 'var(--text-main)'}}>Add Book</h2>

        {!selectedBook ? (
          <div>
            <div style={{display: 'flex', gap: '10px', marginBottom: '20px'}}>
              <input 
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title or author..."
                style={inputStyle}
                onKeyDown={(e) => e.key === 'Enter' && searchBooks()}
              />
              <button onClick={searchBooks} style={searchBtn}>
                {loading ? <Loader2 size={20} className="spin" /> : <Search size={20} />}
              </button>
              <button 
                onClick={() => setIsScanning(true)} 
                style={{ ...searchBtn, backgroundColor: 'var(--bg-app)', color: 'var(--accent)', border: '1px solid var(--accent)' }}
                title="Scan Barcode"
              >
                <Camera size={20} />
              </button>
            </div>

            <div style={moodSectionStyle}>
              <div style={moodLabelStyle}>Find a Vibe</div>
              <div style={moodGridStyle}>
                {MOODS.map((mood) => (
                  <button
                    type="button"
                    key={mood.key}
                    onClick={() => searchByMood(mood)}
                    style={activeMood === mood.key ? activeMoodChip : moodChip}
                  >
                    <span style={{marginRight: '4px'}}>{mood.emoji}</span>{mood.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={resultList}>
              {results.map((item) => (
                <div key={item.id} onClick={() => handleSelect(item)} style={resultItem}>
                  {(item.volumeInfo.imageLinks?.smallThumbnail || item.volumeInfo.cover) && (
                    <img src={item.volumeInfo.imageLinks?.smallThumbnail || item.volumeInfo.cover} alt="cover" style={{width: '40px', height: '60px', objectFit: 'cover', marginRight: '10px', borderRadius: '4px'}} />
                  )}
                  <div>
                    <div style={{fontWeight: 'bold', color: 'var(--text-main)', fontSize: '0.9rem'}}>
                      {item.volumeInfo.title}
                      {item.fromCache && <span style={{fontSize: '0.6rem', color: 'var(--accent)', marginLeft: '5px'}}>(Cached)</span>}
                    </div>
                    <div
                      style={{fontSize: '0.8rem', color: 'var(--text-soft)', textDecoration: 'underline', textDecorationStyle: 'dotted'}}
                      title="See all books by this author"
                      onClick={(e) => {
                        e.stopPropagation();
                        const authorName = Array.isArray(item.volumeInfo.authors) ? item.volumeInfo.authors[0] : item.volumeInfo.author;
                        fetchAuthorBooks(authorName);
                      }}
                    >
                      {Array.isArray(item.volumeInfo.authors) ? item.volumeInfo.authors.join(', ') : item.volumeInfo.author}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={selectedPreview}>
              <img src={selectedBook.cover} alt="cover" style={{height: '80px', borderRadius: '4px'}} />
              <div>
                <h3 style={{margin: '0 0 5px 0', fontSize: '1rem', color: 'var(--text-main)'}}>{selectedBook.title}</h3>
                <p style={{margin: 0, color: 'var(--text-soft)', fontSize: '0.9rem'}}>{selectedBook.author}</p>
                <button onClick={() => setSelectedBook(null)} style={changeBtn}>Change</button>
              </div>
            </div>

            <div style={formGroup}>
              <label style={labelStyle}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
                <option value="tbr">Want to Read</option>
                <option value="reading">Reading</option>
                <option value="read">Finished</option>
                <option value="owned">In Collection (Owned)</option>
              </select>
            </div>

            <div style={formGroup}>
              <label style={labelStyle}>Format</label>
              <div style={formatToggleContainer}>
                <button type="button" onClick={() => setFormat('physical')} style={format === 'physical' ? activeFormatBtn : formatBtn}>
                  <Book size={18} /> Physical
                </button>
                <button type="button" onClick={() => setFormat('ebook')} style={format === 'ebook' ? activeFormatBtn : formatBtn}>
                  <Tablet size={18} /> E-Book
                </button>
                <button type="button" onClick={() => setFormat('audio')} style={format === 'audio' ? activeFormatBtn : formatBtn}>
                  <Headphones size={18} /> Audio
                </button>
              </div>
            </div>

            {format === 'audio' ? (
              <div style={formGroup}>
                <label style={labelStyle}>Duration</label>
                <div style={{display: 'flex', gap: '10px'}}>
                  <div style={{flex: 1}}>
                    <input 
                      type="number" 
                      placeholder="Hours" 
                      value={audioHours} 
                      onChange={(e) => setAudioHours(e.target.value)} 
                      style={inputStyle} 
                    />
                    <span style={subLabel}>Hrs</span>
                  </div>
                  <div style={{flex: 1}}>
                    <input 
                      type="number" 
                      placeholder="Mins" 
                      value={audioMinutes} 
                      onChange={(e) => setAudioMinutes(e.target.value)} 
                      style={inputStyle} 
                    />
                    <span style={subLabel}>Mins</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={formGroup}>
                <label style={labelStyle}>Total Pages</label>
                <input 
                  type="number" 
                  value={totalPages} 
                  onChange={(e) => setTotalPages(e.target.value)} 
                  style={inputStyle} 
                />
              </div>
            )}

            {status === 'read' && (
              <div style={formGroup}>
                <label style={labelStyle}>Rating (1-5)</label>
                <div style={ratingContainer}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span 
                      key={star} 
                      onClick={() => setRating(star)}
                      style={{
                        cursor: 'pointer',
                        fontSize: '24px',
                        color: star <= rating ? '#ffb74d' : '#ddd',
                        transition: 'color 0.2s'
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button type="submit" style={submitBtn}>Add to Library</button>
          </form>
        )}
      </div>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>

      {isScanning && (
        <BarcodeScanner 
          onScan={handleBarcodeScan} 
          onClose={() => setIsScanning(false)} 
        />
      )}
    </div>
  );
};

// Styles
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000
};

const modalStyle = {
  backgroundColor: 'var(--bg-card)',
  padding: '20px', borderRadius: '12px',
  width: '90%', maxWidth: '400px',
  position: 'relative',
  maxHeight: '90vh', overflowY: 'auto'
};

const closeBtn = {
  position: 'absolute', top: '15px', right: '15px',
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)'
};

const inputStyle = {
  width: '100%', padding: '10px', borderRadius: '8px',
  border: '1px solid var(--border)', marginBottom: '5px',
  backgroundColor: 'var(--bg-app)', color: 'var(--text-main)',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '4px', color: 'var(--text-soft)'
};

const searchBtn = {
  padding: '10px', borderRadius: '8px', border: 'none',
  backgroundColor: 'var(--accent)', color: 'white', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const resultList = { marginTop: '10px', maxHeight: '300px', overflowY: 'auto' };

const resultItem = {
  display: 'flex', alignItems: 'center', padding: '10px',
  borderBottom: '1px solid var(--border)', cursor: 'pointer'
};

const moodSectionStyle = { marginBottom: '15px' };

const moodLabelStyle = {
  fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-soft)'
};

const moodGridStyle = {
  display: 'flex', flexWrap: 'wrap', gap: '8px'
};

const moodChip = {
  padding: '6px 12px', borderRadius: '999px', border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', cursor: 'pointer',
  fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center'
};

const activeMoodChip = {
  ...moodChip,
  backgroundColor: 'var(--accent-soft)', borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 'bold'
};

const selectedPreview = {
  display: 'flex', gap: '15px', padding: '10px',
  backgroundColor: 'var(--bg-app)', borderRadius: '8px', marginBottom: '15px'
};

const changeBtn = {
  background: 'none', border: 'none', color: 'var(--accent)',
  textDecoration: 'underline', cursor: 'pointer', fontSize: '0.8rem', padding: 0
};

const formGroup = { marginBottom: '15px' };

const submitBtn = {
  width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
  backgroundColor: 'var(--accent)', color: 'white', fontWeight: 'bold', cursor: 'pointer',
  marginTop: '10px'
};

const ratingContainer = { display: 'flex', gap: '5px' };

const formatToggleContainer = {
  display: 'flex',
  gap: '10px',
  marginTop: '5px'
};

const formatBtn = {
  flex: 1,
  padding: '8px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-soft)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  fontSize: '0.8rem'
};

const activeFormatBtn = {
  ...formatBtn,
  backgroundColor: 'var(--accent-soft)',
  borderColor: 'var(--accent)',
  color: 'var(--accent)',
  fontWeight: 'bold'
};

const subLabel = {
  fontSize: '0.7rem',
  color: 'var(--text-soft)',
  marginLeft: '4px'
};

export default AddBookModal;
